import {useMemo} from "react";
import {CloseButton, Drawer, Text} from "@mantine/core";
import {useTranslation} from "react-i18next";
import type {ChecklistToc} from "../../types";
import {useComment} from "./CommentContext";
import {CommentGroup} from "./CommentGroup";
import {dibAppApi} from "../../../../dibAppApi/dibAppApi";
import styles from "./comment-panel.module.css";

type CommentPanelProps = {
    topicId: string;
    collectionId: string;
    viewFilteredToc: ChecklistToc;
    effectiveRootNodeId: string;
};

export const CommentPanel = ({
    topicId,
    collectionId,
    viewFilteredToc,
    effectiveRootNodeId,
}: CommentPanelProps) => {
    const {t} = useTranslation();
    const {isOpen, focusedNode, closePanel} = useComment();

    const {data: commentsData} = dibAppApi.endpoints.getComments.useQuery(
        {collectionId},
        {skip: !collectionId},
    );

    const allItems = useMemo(() => {
        const items: {id: string; title: string}[] = [];
        const traverse = (nodeId: string) => {
            const node = viewFilteredToc[nodeId];
            if (!node) return;
            items.push({id: nodeId, title: node.title || ""});
            node.children?.forEach(traverse);
        };
        if (effectiveRootNodeId) traverse(effectiveRootNodeId);
        return items;
    }, [viewFilteredToc, effectiveRootNodeId]);

    const commentItemIds = useMemo(
        () =>
            allItems
                .map((item) => item.id)
                .filter((id) => {
                    const isFocused = focusedNode?.id === id;
                    const hasComment =
                        !!commentsData?.elementdata?.[id];
                    return isFocused || hasComment;
                }),
        [allItems, commentsData, focusedNode],
    );

    const titles = useMemo(() => {
        const map: Record<string, string> = {};
        for (const item of allItems) {
            map[item.id] = item.title;
        }
        return map;
    }, [allItems]);

    const handleClose = () => {
        // Saving is blur-driven. Escape closes the drawer without moving focus,
        // so commit whatever editor still has it before the content unmounts.
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        closePanel();
    };

    return (
        <Drawer
            opened={isOpen}
            onClose={handleClose}
            position="right"
            size="lg"
            trapFocus={false}
            lockScroll={false}
            withOverlay={false}
            withCloseButton={false}
        >
            <header className={styles.panelHeader}>
                <Text fw={600} size="lg">
                    {t("Word.Comments")}
                </Text>
                <CloseButton onClick={handleClose} />
            </header>
            <ol className={styles.commentList}>
                {commentsData
                    ? commentItemIds.map((id) => (
                          <CommentGroup
                              key={id}
                              id={id}
                              title={titles[id] ?? ""}
                          />
                      ))
                    : null}
            </ol>
        </Drawer>
    );
};
